import { useEffect, useState, type ChangeEvent } from "react";
import type { MomentListItem } from "../../data/momentOptions";
import { SectionBox, inputCls } from "../shared";
import { MOMENT_IMAGE_ALLOWED_SIZES } from "../../utils/imageValidator";
import ImagePickerArea from "./ImagePickerArea";

type Props = {
  messageTitle: string;
  listItems: MomentListItem[];
  onMessageTitleChange: (value: string) => void;
  onListItemChange: (id: string, key: "description" | "pcLandingUrl" | "mobileLandingUrl", value: string) => void;
  onListItemImageChange: (id: string, event: ChangeEvent<HTMLInputElement>) => void;
  onListItemLibrarySelect?: (id: string, imgUrl: string) => void;
  onAddListItem: () => void;
  onRemoveListItem: (id: string) => void;
};

export default function WideListFields({
  messageTitle, listItems,
  onMessageTitleChange, onListItemChange, onListItemImageChange,
  onListItemLibrarySelect,
  onAddListItem, onRemoveListItem,
}: Props) {
  const [listPcEnabled, setListPcEnabled] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setListPcEnabled(
      Object.fromEntries(listItems.map((item) => [item.id, item.pcLandingUrl.trim().length > 0]))
    );
  }, [listItems]);

  return (
    <>
      {/* 헤더 */}
      <SectionBox label="헤더" required>
        <label className="tw-block tw-text-xs tw-text-gray-500">
          <span className="tw-text-red-500 tw-mr-1">*</span>
          메인 타이틀
        </label>
        <input
          value={messageTitle}
          onChange={(e) => onMessageTitleChange(e.target.value)}
          maxLength={20}
          placeholder="메인 타이틀을 입력해주세요."
          className={inputCls}
        />
        <p className="tw-text-xs tw-text-gray-400">{messageTitle.length} / 20</p>
      </SectionBox>

      {/* 소재 (리스트 아이템) */}
      <SectionBox label="소재" required>
        <div className="tw-space-y-3">
          <div className="tw-flex tw-justify-between tw-items-center">
            <span className="tw-text-xs tw-text-gray-500">3~5개 등록</span>
            <button
              type="button"
              onClick={onAddListItem}
              disabled={listItems.length >= 5}
              className="tw-text-xs tw-bg-blue-50 tw-text-blue-600 tw-px-3 tw-py-1 tw-rounded-lg tw-border tw-border-blue-200 disabled:tw-opacity-40"
            >
              + 추가
            </button>
          </div>
          {listItems.map((item, index) => {
            const isHeader = index === 0;
            const allowedSizes = isHeader
              ? MOMENT_IMAGE_ALLOWED_SIZES.WIDE_LIST_HEADER
              : MOMENT_IMAGE_ALLOWED_SIZES.WIDE_LIST_ITEM;
            const sizeHint = isHeader ? "800×400px" : "800×800px";

            return (
              <div key={item.id} className="tw-border tw-border-gray-200 tw-rounded-xl tw-overflow-hidden">
                <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-bg-gray-50 tw-border-b tw-border-gray-200">
                  <span className="tw-text-sm tw-font-semibold tw-text-gray-800">
                    리스트 {index + 1}
                    {isHeader && <span className="tw-text-xs tw-text-gray-400 tw-font-normal tw-ml-1.5">(헤더 이미지)</span>}
                  </span>
                  <button
                    type="button"
                    onClick={() => onRemoveListItem(item.id)}
                    disabled={listItems.length <= 3}
                    className="tw-text-xs tw-bg-red-50 tw-text-red-400 tw-px-3 tw-py-1 tw-rounded-lg tw-border tw-border-red-200 disabled:tw-opacity-30"
                  >
                    삭제
                  </button>
                </div>
                <div className="tw-p-4 tw-space-y-3">
                  {(() => {
                    const descriptionLimit = isHeader ? 25 : 30;
                    return (
                      <>
                        <label className="tw-block tw-text-xs tw-text-gray-500">
                          <span className="tw-text-red-500 tw-mr-1">*</span>
                          홍보 문구
                        </label>
                        <input
                          value={item.description}
                          onChange={(e) => onListItemChange(item.id, "description", e.target.value.replace(/\n/g, "").slice(0, descriptionLimit))}
                          placeholder={`홍보 문구 (최대 ${descriptionLimit}자)`}
                          maxLength={descriptionLimit}
                          className={inputCls}
                        />
                        <div className="tw-flex tw-justify-end tw-text-xs tw-text-gray-400">
                          <span>{item.description.length} / {descriptionLimit}</span>
                        </div>
                      </>
                    );
                  })()}

                  <div>
                    <label className="tw-block tw-text-xs tw-text-gray-500 tw-mb-1.5">
                      <span className="tw-text-red-500 tw-mr-1">*</span>
                      홍보 이미지
                    </label>
                    <ImagePickerArea
                      imageName={item.imageName}
                      sizeHint={sizeHint}
                      allowedSizes={allowedSizes}
                      onFileChange={(e) => onListItemImageChange(item.id, e)}
                      onLibrarySelect={onListItemLibrarySelect ? (url) => onListItemLibrarySelect(item.id, url) : undefined}
                      compact
                    />
                  </div>

                  <div className="tw-space-y-1.5">
                    <label className="tw-block tw-text-xs tw-text-gray-500">
                      <span className="tw-text-red-500 tw-mr-1">*</span>
                      모바일 랜딩 URL
                    </label>
                    <input
                      value={item.mobileLandingUrl}
                      onChange={(e) => onListItemChange(item.id, "mobileLandingUrl", e.target.value)}
                      placeholder="모바일 랜딩 URL"
                      className={inputCls}
                    />
                  </div>
                  <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600 tw-cursor-pointer tw-w-fit">
                    <input
                      type="checkbox"
                      checked={listPcEnabled[item.id] ?? false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setListPcEnabled((prev) => ({ ...prev, [item.id]: checked }));
                        if (!checked) onListItemChange(item.id, "pcLandingUrl", "");
                      }}
                      className="tw-w-3.5 tw-h-3.5"
                    />
                    PC 링크
                  </label>
                  {listPcEnabled[item.id] && (
                    <div className="tw-space-y-1.5">
                      <label className="tw-block tw-text-xs tw-text-gray-500">PC 랜딩 URL</label>
                      <input
                        value={item.pcLandingUrl}
                        onChange={(e) => onListItemChange(item.id, "pcLandingUrl", e.target.value)}
                        placeholder="PC 랜딩 URL"
                        className={inputCls}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </SectionBox>
    </>
  );
}
